import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Field, fieldInputCls } from "@/components/Field";
import { LangSwitch } from "@/components/LangSwitch";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { api, formatApiError } from "@/lib/api";

const HERO = "/images/race.jpg";

function googleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/events";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const AuthForm = () => {
  const { t } = useT();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(`/auth/${mode}`, payload);
      setUser(data);
      navigate("/events");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="auth-form">
      <div className="flex border-b border-[#282D3D]">
        {["login", "register"].map((m) => (
          <button type="button" key={m} data-testid={`auth-tab-${m}`} onClick={() => setMode(m)}
            className={`flex-1 py-3 font-heading uppercase text-lg tracking-wide border-b-2 transition-colors duration-200 ${mode === m ? "border-[#FF3B30] text-white" : "border-transparent text-slate-500 hover:text-slate-300"}`}>
            {t(m)}
          </button>
        ))}
      </div>
      {mode === "register" && (
        <Field label={t("name")}>
          <Input data-testid="auth-name-input" required value={form.name} onChange={set("name")} className={fieldInputCls} />
        </Field>
      )}
      <Field label={t("email")}>
        <Input data-testid="auth-email-input" type="email" required value={form.email} onChange={set("email")} className={fieldInputCls} />
      </Field>
      <Field label={t("password")}>
        <Input data-testid="auth-password-input" type="password" required minLength={6} value={form.password} onChange={set("password")} className={fieldInputCls} />
      </Field>
      {error && <p data-testid="auth-error" className="text-sm text-[#FF3B30] font-mono">{error}</p>}
      <Button data-testid="auth-submit-btn" disabled={busy} type="submit"
        className="w-full h-11 rounded-sm bg-[#FF3B30] hover:bg-[#e0332a] font-heading uppercase text-lg tracking-wide transition-transform duration-150 active:scale-[0.98]">
        {mode === "login" ? t("sign_in") : t("create_account")}
      </Button>
      <div className="flex items-center gap-3 text-xs font-mono uppercase text-slate-600">
        <span className="h-px flex-1 bg-[#282D3D]" /> {t("or")} <span className="h-px flex-1 bg-[#282D3D]" />
      </div>
      <Button data-testid="google-login-btn" type="button" variant="outline" onClick={googleLogin}
        className="w-full h-11 rounded-sm bg-transparent border-[#282D3D] hover:bg-[#181B26] hover:text-white">
        <svg className="w-4 h-4 mr-2" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
        {t("google")}
      </Button>
    </form>
  );
};

export default function Login() {
  const { user } = useAuth();
  const { t } = useT();
  if (user) return <Navigate to="/events" replace />;
  return (
    <div className="grain min-h-screen grid lg:grid-cols-[1.2fr_1fr] bg-[#090A0F] text-slate-200">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="Kart racing" data-testid="login-hero-image" className="absolute inset-0 w-full h-full object-cover object-[65%_center]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#090A0F] via-[#090A0F]/50 to-transparent" />
        <div className="absolute bottom-0 left-0 p-12 max-w-xl fade-up">
          <span className="checker block w-14 h-4 mb-6" />
          <h1 className="font-heading font-extrabold uppercase text-6xl leading-[0.9] tracking-tight">
            Kart Setup <span className="text-[#FF3B30]">&</span><br />Data Analisys
          </h1>
          <p data-testid="login-hero-subtitle" className="mt-2 font-mono text-sm uppercase tracking-[0.25em] text-slate-400">by RacingDreams</p>
          <p data-testid="login-hero-claim" className="mt-5 font-heading font-bold uppercase text-2xl tracking-wide text-[#FFB800]">{t("claim")}</p>
          <p className="mt-2 text-slate-300 text-lg">{t("tagline")}</p>
        </div>
      </div>
      <div className="relative z-10 flex flex-col">
        <div className="lg:hidden relative h-52 overflow-hidden">
          <img src={HERO} alt="Kart racing" data-testid="login-mobile-image" className="absolute inset-0 w-full h-full object-cover object-[65%_center]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#090A0F] to-transparent" />
          <div className="absolute top-4 right-4"><LangSwitch /></div>
        </div>
        <div className="hidden lg:flex justify-end p-6"><LangSwitch /></div>
        <div className="flex-1 flex items-center justify-center px-6 pb-16">
          <div className="w-full max-w-md fade-up">
            <div className="lg:hidden mb-8">
              <span className="checker block w-10 h-3 mb-4" />
              <h1 className="font-heading font-extrabold uppercase text-4xl leading-none">
                Kart Setup <span className="text-[#FF3B30]">&</span> Data Analisys
              </h1>
              <p data-testid="login-mobile-subtitle" className="mt-2 font-mono text-xs uppercase tracking-[0.25em] text-slate-400">by RacingDreams</p>
              <p data-testid="login-mobile-claim" className="mt-3 font-heading font-bold uppercase text-lg tracking-wide text-[#FFB800]">{t("claim")}</p>
              <p className="mt-1 text-slate-400 text-sm">{t("tagline")}</p>
            </div>
            <div className="bg-[#12141C] border border-[#282D3D] rounded-sm p-6 sm:p-8">
              <AuthForm />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
